import { createFileRoute, type ToOptions } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import z from 'zod'

import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { RealEstateCalendarTab } from '~/components/routes/development/real-estate/calendar/real-estate-calendar-tab'
import { RealEstateContactsTab } from '~/components/routes/development/real-estate/contacts/real-estate-contacts-tab'
//import { RealEstateEnseignesTab } from '~/components/routes/development/real-estate/enseignes/real-estate-enseignes-tab'
import { RealEstateInterestZonesTab } from '~/components/routes/development/real-estate/interest-zones/real-estate-interest-zones-tab'
import { KanbanFullArea } from '~/components/routes/development/real-estate/kanban/kanban-full-area'
import { RealEstateMapTab } from '~/components/routes/development/real-estate/map/real-estate-map-tab'
import { SavedMapsTab } from '~/components/routes/development/real-estate/saved-maps/saved-maps-tab'
import { StoreProjectSearchFilterCreate } from '~/components/routes/development/real-estate/store-project/store-project-search-filter-create'
import {
  ABANDONED_FILTER_VALUE,
  type StatusFilterValue,
} from '~/components/routes/development/real-estate/store-project/statuses-filter-chip'
import type {
  ProjectStatus,
  RealEstateProject,
} from '~/components/routes/development/real-estate/store-project/types'
import { RealEstateTasksTab } from '~/components/routes/development/real-estate/tasks/tasks-tab'
import {
  REAL_ESTATE_PAGE_READ,
  REAL_ESTATE_TABS,
} from '~/components/routes/development/real-estate/tabs'
import { useRealEstatePermissions } from '~/components/routes/development/real-estate/use-real-estate-permissions'
import { withRoutePermissions } from '~/utils/rbac'
import { useAuthorizedTabs, useFirstAuthorizedTab } from '~/utils/rbacTabs'

const tabValues = [
  'map',
  'kanban',
  'tasks',
  'calendar',
  'contacts',
  //'enseignes',
  'interest-zones',
  'cartes-validees',
] as const
export const Route = createFileRoute('/_authenticated/development/real-estate/$tab')({
  staticData: { permission: REAL_ESTATE_PAGE_READ },
  component: RealEstatePage,
  params: {
    parse: z.object({ tab: z.enum(tabValues) }).parse,
  },
})
withRoutePermissions(Route)

const tabOptions = {
  map: {
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'map' } },
    Component: RealEstateMapTab,
  },
  kanban: {
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'kanban' } },
    Component: KanbanFullArea,
  },
  tasks: {
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'tasks' } },
    Component: RealEstateTasksTab,
  },
  calendar: {
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'calendar' } },
    Component: RealEstateCalendarTab,
  },
  contacts: {
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'contacts' } },
    Component: RealEstateContactsTab,
  },
  //enseignes: {
  //label: 'Enseignes concurrentes',
  //toOptions: { to: '/development/real-estate/$tab', params: { tab: 'enseignes' } },
  //Component: RealEstateEnseignesTab,
  //},
  'interest-zones': {
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'interest-zones' } },
    Component: RealEstateInterestZonesTab,
  },
  'cartes-validees': {
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'cartes-validees' } },
    Component: SavedMapsTab,
  },
} as const satisfies Record<
  (typeof tabValues)[number],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { toOptions: ToOptions; Component: React.ComponentType<any> }
>

function RealEstatePage() {
  const realEstatePermissions = useRealEstatePermissions()

  const navigate = Route.useNavigate()
  const { tab: requestedTab } = Route.useParams()

  const authorizedTabs = useAuthorizedTabs(REAL_ESTATE_TABS)
  const firstAuthorizedTab = useFirstAuthorizedTab(authorizedTabs)
  // Un onglet interdit reste affiché (désactivé + tooltip), mais on n'en montre jamais le contenu :
  // on retombe sur le premier onglet accessible.
  const tab = authorizedTabs.find((item) => item.value === requestedTab)?.authorized
    ? requestedTab
    : (firstAuthorizedTab ?? requestedTab)

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatuses, setSelectedStatuses] = useState<StatusFilterValue[]>([])
  const [hideProjects, setHideProjects] = useState(false)

  // Le filtre 'Abandonné' est porté par le state partagé mais la carte n'a pas de
  // notion d'abandon — on lui passe uniquement les vrais statuts.
  const mapSelectedStatuses = selectedStatuses.filter(
    (value): value is ProjectStatus => value !== ABANDONED_FILTER_VALUE,
  )

  const newProjectHandlerRef = useRef<(() => void) | null>(null)
  const registerNewProjectHandler = useRef((handler: (() => void) | null): void => {
    newProjectHandlerRef.current = handler
  }).current

  const exportProjectsRef = useRef<(() => RealEstateProject[]) | null>(null)
  const registerExportProjects = useRef((getter: (() => RealEstateProject[]) | null): void => {
    exportProjectsRef.current = getter
  }).current

  const isProjectFilterTab = tab === 'map' || tab === 'kanban'
  const [headerSlotContent, setHeaderSlotContent] = useState<React.ReactNode>(null)

  function renderCurrentTab(): React.ReactNode {
    if (tab === 'map') {
      return (
        <RealEstateMapTab
          key={tab}
          searchQuery={searchQuery}
          selectedStatuses={mapSelectedStatuses}
          hideProjects={hideProjects}
          onRegisterNewProject={registerNewProjectHandler}
          onRegisterExportProjects={registerExportProjects}
        />
      )
    }
    if (tab === 'kanban') {
      return (
        <KanbanFullArea
          key={tab}
          searchQuery={searchQuery}
          selectedStatuses={selectedStatuses}
          onRegisterNewProject={registerNewProjectHandler}
          onRegisterExportProjects={registerExportProjects}
        />
      )
    }
    if (tab === 'tasks') {
      return <RealEstateTasksTab key={tab} onHeaderChange={setHeaderSlotContent} />
    }
    if (tab === 'calendar') {
      return <RealEstateCalendarTab key={tab} onHeaderChange={setHeaderSlotContent} />
    }
    if (tab === 'contacts') {
      return <RealEstateContactsTab key={tab} onHeaderChange={setHeaderSlotContent} />
    }
    // Fallback : tabs sans props additionnelles (enseignes, interest-zones, …).
    const TabComponent = tabOptions[tab].Component
    return <TabComponent key={tab} />
  }

  const showHeaderSlot = tab === 'tasks' || tab === 'calendar' || tab === 'contacts'

  return (
    <>
      <AppHeader className="mb-4">
        <Title className="text-foreground text-[32px] font-extrabold leading-[normal] font-brand">
          Développement foncier
        </Title>
      </AppHeader>

      <AppContainer>
        <div
          style={{ marginBottom: '20px' }}
          className="flex flex-wrap items-center justify-between gap-3"
        >
          <AppTabs
            tabs={authorizedTabs.map((item) => ({
              label: item.label,
              value: item.value,
              disabled: !item.authorized,
              tooltip: item.tooltip,
            }))}
            value={tab}
            onTabChange={(v) => {
              if (!authorizedTabs.find((item) => item.value === v)?.authorized) return
              navigate({ ...tabOptions[v].toOptions, search: (prev) => ({ ...prev }) })
            }}
          />
          {isProjectFilterTab && (
            <StoreProjectSearchFilterCreate
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              onNewProject={() => newProjectHandlerRef.current?.()}
              canManage={realEstatePermissions.canCreateProject()}
              selectedStatuses={selectedStatuses}
              onStatusFilterChange={setSelectedStatuses}
              getProjectsToExport={() => exportProjectsRef.current?.() ?? []}
              hideProjects={hideProjects}
              onToggleHideProjects={
                tab === 'map' ? () => setHideProjects((value) => !value) : undefined
              }
            />
          )}
          {showHeaderSlot && headerSlotContent}
        </div>

        {renderCurrentTab()}
      </AppContainer>
    </>
  )
}
